import DatePicker from "@/components/DatePicker";
import InputGroup from "@/components/InputGroup";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { CompanyUpdate } from "@/schema/console/companies/CompanyUpdate";
import IndustrySelector from "@/src/console/(auth)/companies/_components/IndustrySelector";
import { format } from "date-fns";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

function CompanyForm() {
  const { t } = useTranslation();
  const {
    register,
    control,
    formState: { errors },
    setValue,
  } = useFormContext<CompanyUpdate>();

  return (
    <div className="mx-6 my-8 flex flex-col gap-4">
      <div className="flex gap-6">
        <div className="w-[320px] text-lg font-bold">{t("company-info")}</div>
        <div className="my-card grid w-full grid-cols-2 gap-6 px-5 py-4">
          <InputGroup
            // className="col-span-full"
            label={t("company-name")}
            required
            error={errors.company_name?.message}
            register={register("company_name", { required: true })}
          />
          <InputGroup
            label={t("company-name-kana")}
            error={errors.company_name_kana?.message}
            register={register("company_name_kana")}
          />
          <InputGroup
            label={t("representative-name")}
            required
            error={errors.ceo_name?.message}
            register={register("ceo_name", { required: true })}
          />
          <InputGroup
            label={t("representative-name-kana")}
            error={errors.ceo_name_kana?.message}
            register={register("ceo_name_kana")}
          />
          <InputGroup
            type="text"
            label={t("position")}
            className="col-span-full"
            error={errors.position?.message}
            register={register("position")}
          />
          <InputGroup
            className="col-span-full"
            label={t("location")}
            required
            error={errors.company_address?.message}
            register={register("company_address")}
          />
          <InputGroup
            label={t("phone")}
            required
            error={errors.phone_number?.message}
            register={register("phone_number")}
          />
          <InputGroup
            label={t("url")}
            required
            error={errors.homepage_url?.message}
            register={register("homepage_url")}
          />
        </div>
      </div>
      <div className="h-px bg-cool-gray-300" />
      <div className="flex gap-6">
        <div className="w-[320px] text-lg font-bold">
          {t("coporate-profile")}
        </div>
        <div className="my-card grid w-full grid-cols-2 gap-6 px-5 py-4">
          <IndustrySelector
            className="col-span-full"
            onIndustryCodeChange={(code: string) =>
              setValue("industry_code", code)
            }
            error={errors.industry_code?.message}
          />
          <div className="flex flex-col gap-1">
            <div>{t("established-date")}</div>
            <Controller
              control={control}
              name="established_date"
              render={({ field }) => (
                <DatePicker
                  className="w-full border border-cool-gray-300"
                  value={field.value ? new Date(field.value) : undefined}
                  error={errors.established_date?.message}
                  onDateChange={(date) => {
                    field.onChange(
                      date ? format(date, "yyyy-MM-dd") : undefined,
                    );
                  }}
                />
              )}
            />
          </div>
          <InputGroup
            type="email"
            required
            label={t("email")}
            error={errors.email?.message}
            register={register("email")}
          />
          <InputGroup
            type="text"
            required
            label={t("corporate-number")}
            error={errors.corporate_number?.message}
            register={register("corporate_number")}
          />
          <InputGroup
            type="number"
            label={t("capital")}
            error={errors.funds?.message}
            register={register("funds")}
          />
          <div className="self-center">
            <Controller
              control={control}
              name="is_listed"
              render={({ field }) => (
                <div className="flex items-center gap-2">
                  <Label label={t("listed-market")} htmlFor="is_listed" />
                  <Checkbox
                    id="is_listed"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </div>
              )}
            />
          </div>
          <InputGroup
            type="text"
            label={t("market-name")}
            error={errors.market_name?.message}
            register={register("market_name")}
          />
          <InputGroup
            type="number"
            label={t("num-of-employees")}
            required
            className="col-span-full"
            error={errors.employee_count?.message}
            register={register("employee_count")}
          />
          <InputGroup
            type="number"
            label={t("foreign-employees")}
            error={errors.foreign_employee_count?.message}
            register={register("foreign_employee_count")}
          />
          <InputGroup
            type="number"
            label={t("permanent-employees")}
            error={errors.permanent_employee_count?.message}
            register={register("permanent_employee_count")}
          />
          <InputGroup
            type="number"
            label={t("contract-employees")}
            error={errors.contract_employee_count?.message}
            register={register("contract_employee_count")}
          />
          <InputGroup
            type="number"
            label={t("temporary-employees")}
            error={errors.temporary_employee_count?.message}
            register={register("temporary_employee_count")}
          />
          <InputGroup
            type="number"
            label={t("contractor-employees")}
            error={errors.contractor_count?.message}
            register={register("contractor_count")}
          />
          <InputGroup
            type="number"
            label={t("part-time-employees")}
            error={errors.part_time_employee_count?.message}
            register={register("part_time_employee_count")}
          />
          <InputGroup
            type="number"
            label={t("intern-employees")}
            error={errors.intern_count?.message}
            register={register("intern_count")}
          />
          <InputGroup
            type="number"
            label={t("specialist-employees")}
            error={errors.specialist_count?.message}
            register={register("specialist_count")}
          />
          <InputGroup
            type="text"
            className="col-span-full"
            label={t("employee-countries")}
            error={errors.employee_countries?.message}
            register={register("employee_countries")}
          />
        </div>
      </div>
    </div>
  );
}

export default CompanyForm;
